import React, { FC } from 'react';
import Fade from 'react-reveal/Fade';
import { TContentHubHeroBannerProps } from './types/productHeroBanner.types';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import useExperienceEditorPlaceholder from '../../hooks/useExperienceEditorPlaceholder';
import { isEditorActive, RichText, Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { ProductHeroBannerModel } from './models/productHeroBanner.model';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';

import '../../styles/@shared/hero-banner.scss';
import './styles.scss';
import CTA from '../@shared/CTA';
import ctaTypes from '../../enums/CTA.enums';
import Picture from '../@shared/picture';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import { targetLinkdsk } from '../../utils/targetDskLink';
import useMediaQuery from '../../hooks/useMediaQuery';
import breakpoints from '../../constants/breakpoints.constants';
import { shouldPioritizeComponentResources } from '../../utils/component.utils';
import useGetBuildAndPriceLink from '../../hooks/useGetBuildAndPriceLink';
import { appendRootToInternalLink } from '../../utils/appendRootToInternalLink.utils';
import DictionaryTerm from '../@shared/dictionaryTerm';

const ProductHeroBanner: FC<TContentHubHeroBannerProps> = (props) => {
  const apiResponse = isEmptyResponse(props);
  const model = new ProductHeroBannerModel(props);
  const isPlaceholderShown = useExperienceEditorPlaceholder(props);
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints['tbl-p']}px)`);
  const placeholderData = props.sitecoreContext?.route?.placeholders['jss-main'] || [];
  const buildAndPriceUrl = useGetBuildAndPriceLink('line');

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  return (
    <div className="hero-banner product-hero-banner" data-text-color={model?.textColor?.toLowerCase()}>
      {/* Need to update useHeroBanner hook if the images sent to Picture component  are changed */}

      <Picture
        productImageProps={{
          desktopRendition: model.desktopRendition,
          desktopSDRendition: model.desktopSDRendition,
          tabletRendition: model.tabletRendition,
          tabletLandscapeRendition: model.tabletLandscapeRendition,
          mobileRendition: model.mobileRendition,
          imageClassName: 'hero-banner__image',
          pictureClassName: 'hero-banner__image-wrapper',
          alt: model.title,
          prioritize: shouldPioritizeComponentResources(placeholderData, 'ProductHeroBanner'),
        }}
      />
      <div className="hero-banner__content">
        <Fade bottom duration={1000} delay={1000}>
          <Text
            tag="p"
            field={{ value: model.eyebrow }}
            className={`hero-banner__eyebrow hero-banner--text-color-${model?.textColor}`}
            editable={false}
          />
        </Fade>
        <Fade bottom duration={1000} delay={2000}>
          <Text
            tag="h1"
            field={{ value: model.title }}
            className={`hero-banner__title hero-banner--text-color-${model?.textColor}`}
            editable={false}
          />
        </Fade>
        <Fade bottom duration={1000} delay={3000}>
          <RichText
            tag="div"
            field={{ value: model.description }}
            className={`hero-banner__description hero-banner--text-color-${model?.textColor} rich-text`}
            editable={false}
          />
        </Fade>
        {
          model?.enableBuildPriceCTA && buildAndPriceUrl && (
            <Fade delay={4000}>
              <span className={'hero-banner__link hero-banner__link--primary'}>
                <CTA type={ctaTypes.primaryOrange}
                  href={appendRootToInternalLink(buildAndPriceUrl)}
                >
                  <DictionaryTerm keyword={'cnhi-build-price'} />
                </CTA>
              </span>
            </Fade>
          )
        }
        {model?.primaryLink?.label && model?.primaryLink?.url && (
          <Fade bottom duration={1000} delay={4000}>
            <span className={'hero-banner__link hero-banner__link--primary'}>
              <CTA
                type={ctaTypes.primaryOrange}
                href={model.primaryCTAUrl}
                target={targetLinkdsk({ value: { href: model?.primaryLink?.url, target: model?.primaryLink?.target } }, isDesktop)}
              >
                {model.primaryLink.label}
              </CTA>
            </span>
          </Fade>
        )}
        {model?.secondaryLink?.label && model?.secondaryLink?.url && (
          <Fade bottom duration={1000} delay={4000}>
            <span className={'hero-banner__link hero-banner__link--secondary'}>
              <CTA
                type={ctaTypes.primaryWhite}
                href={model.secondaryCTAUrl}
                target={targetLinkdsk({ value: { href: model?.secondaryLink?.url, target: model?.secondaryLink?.target } }, isDesktop)}
              >
                {model.secondaryLink.label}
              </CTA>
            </span>
          </Fade>
        )}
      </div>
    </div>
  );
};

export default withSitecoreContext()(ProductHeroBanner);
